import React, { Fragment, useState } from 'react';
import {
    Box,
    Chip,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Theme,
    Typography,
    useMediaQuery,
} from '@mui/material';
import { CustomerType, LabClient } from 'models/client-directory';
import SubdirectoryArrowRightIcon from '@mui/icons-material/SubdirectoryArrowRight';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import { NetworkStatus, useQuery } from '@apollo/client';
import { transportProviderName } from 'utils/transport-provider-name';
import { getRelatedClinicsTemplate } from 'apollo/templates/organization';
import { useDenthubErrorBoundary } from 'components/shared/denthub-error-boundary/denthub-error-boundary-context';
import Loader from 'components/shared/loader/loader.component';
import InfiniteScroll from 'react-infinite-scroll-component';
import useFixMissingScroll from 'utils/use-fix-missing-scroll';

const RelatedClinics = () => {
    const { t: translate } = useTranslation([
        'common',
        'client-directory',
        'orders',
        'transport',
        'transport-providers',
    ]);
    const { id } = useParams<{ id: string }>();
    const isLargeScreen = useMediaQuery((theme: Theme) => theme.breakpoints.up('xl'));
    const navigate = useNavigate();
    const { showBoundary } = useDenthubErrorBoundary();
    const [lastOffset, setLastOffset] = useState(0);

    const {
        data: { relatedClinics } = {},
        loading: relatedClinicsLoading,
        fetchMore,
        networkStatus,
    } = useQuery(getRelatedClinicsTemplate, {
        notifyOnNetworkStatusChange: true,
        variables: {
            clinicId: id,
            offset: 0,
            limit: 30,
        },
        onCompleted: ({ relatedClinics }) => {
            setLastOffset(relatedClinics.length);
        },
        onError(error) {
            showBoundary(error);
        },
    });

    const handleFetchMore = () => {
        fetchMore({
            variables: {
                offset: lastOffset,
            },
        });
    };

    const handleClientClick = (clientId: string) => {
        navigate(`/client-directory/clients/${clientId}/client-details#contactDetails`);
    };

    // Fix for infinite scroll not working on some screen sizes.
    useFixMissingScroll(handleFetchMore, relatedClinics?.length);

    if (relatedClinicsLoading && networkStatus !== NetworkStatus.fetchMore) {
        return <Loader />;
    }

    return (
        <Fragment>
            <Typography variant="h2">{translate('orders:ALL_CLINICS')}</Typography>
            <InfiniteScroll
                dataLength={relatedClinics.length}
                next={handleFetchMore}
                hasMore={true}
                throttleTimeInMs={75}
                loader={undefined}
            >
                <TableContainer component={Paper}>
                    <Table size={!isLargeScreen ? 'small' : 'medium'}>
                        <TableHead>
                            <TableRow>
                                <TableCell>{translate('common:NAME')}</TableCell>
                                <TableCell>{translate('orders:DELIVERY_ADDRESS')}</TableCell>
                                <TableCell>{translate('client-directory:TRANSPORT_PROVIDER')}</TableCell>
                                <TableCell>{translate('client-directory:DELIVERY_INSTRUCTIONS')}</TableCell>
                                <TableCell>{translate('transport:DEADLINE')}</TableCell>
                                <TableCell>{translate('client-directory:EXTERNAL_DEADLINE')}</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {relatedClinics?.map((client: LabClient, index: number) => (
                                <TableRow
                                    key={index}
                                    hover
                                    sx={{ cursor: 'pointer' }}
                                    onClick={() => handleClientClick(client.customer._id)}
                                >
                                    <Fragment>
                                        <TableCell>
                                            {client.customer.customerType === CustomerType.DENTHUB_CONNECTED &&
                                            client.customer.parentClinic?.name ? (
                                                <Box ml={0.25} display="flex" alignItems="center">
                                                    <SubdirectoryArrowRightIcon sx={{ color: 'grey.300' }} />
                                                    <Box ml={0.5}>
                                                        <Box>{client.customer.name}</Box>
                                                        <Typography variant="caption" color="grey.500">
                                                            {`${translate(`client-directory:BELONGS_TO`)}: ${client.customer.parentClinic?.name}`}
                                                        </Typography>
                                                    </Box>
                                                </Box>
                                            ) : (
                                                <Box>
                                                    {client.customer.name}
                                                    {client.customer.isMain ? (
                                                        <Fragment>
                                                            <Chip
                                                                size="small"
                                                                sx={{ marginLeft: 1 }}
                                                                label={translate(`client-directory:MAIN`)}
                                                            />
                                                        </Fragment>
                                                    ) : undefined}
                                                </Box>
                                            )}
                                        </TableCell>
                                        <TableCell>
                                            {client.customer.contact.address.street},{' '}
                                            {client.customer.contact.address.postalCode},{' '}
                                            {client.customer.contact.address.city}
                                        </TableCell>
                                        <TableCell>
                                            {translate(transportProviderName(client.transportProvider))}
                                        </TableCell>
                                        <TableCell>{client.deliveryInstructions || '-'}</TableCell>
                                        <TableCell>{client.internalDeliveryDaysAdvance || '-'}</TableCell>
                                        <TableCell>{client.externalDeliveryDaysAdvance || '-'}</TableCell>
                                    </Fragment>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
            </InfiniteScroll>
        </Fragment>
    );
};

export default RelatedClinics;
